// =============================================================================
// staticSite/siteAssembler — zbiera wszystko, co ma mapa I co jest na WP dzisiaj,
// w jeden SiteModel.
//
// Zrodla:
//   DB:  thematic_maps, websites, thematic_seeds (+ thematic_seed_wp_categories),
//        thematic_nodes, thematic_node_content (engine_stage=published),
//        thematic_writing_profiles (strona autora), map_persona, map_brand_assets,
//        unique_subpages (ready/published), wp_posts (fallback gdy REST padnie)
//   WP REST (publiczne, bez auth): wpisy (+ kategorie per wpis + featured), strony,
//        kategorie (nazwa, opis, LINK -> sciezka). To jest ZRODLO PRAWDY dla
//        kategorii i starych wpisow — a nie stara synchronizacja wp_posts.
//   Edge Fn: live sitemap WP (inwentarz do G3).
// Per artykul generateSchemasForNode. ZASADA URL: link WP wygrywa doslownie.
// =============================================================================

import { supabase } from "@/integrations/supabase/client";
import { generateSchemasForNode } from "@/services/schema/schemaGenerator";
import { logPublic, logError } from "@/services/contentEngine/contentLogger";
import type {
  AuthorModel, BrandModel, CategoryModel, PageModel, SiteModel, StaticSiteSettings, ProgressCallback,
} from "./types";
import { DEFAULT_SITE_SETTINGS } from "./types";
import {
  authorPath, baseUrlForDomain, categoryPath, domainFromUrl, pathForSlug, pathFromUrl, subpagePath, hostOf, isSiteHost,
} from "./urlPolicy";
import { fetchAssetText } from "./assetFetchClient";
import { excerptFromHtml } from "./themes/shared";
import { buildMenuAuto, buildMenuFromWp, validateMenu } from "./menuBuilder";
import {
  fetchWpCategories, fetchWpPages, fetchWpPosts, cleanWpContent, decodeEntities,
  type WpRestCategory, type WpRestPage, type WpRestPost,
} from "./wpRestPublic";

const PREFIX = "STATIC";

export interface AssembleOptions {
  settings?: Partial<StaticSiteSettings>;
  /** Pobierz live sitemap WP przez Edge Function (inwentarz do G3). */
  fetchWpSitemap?: boolean;
  /** Pobierz wpisy / strony / kategorie z WP REST (publiczne). */
  fetchWpRest?: boolean;
  onProgress?: ProgressCallback;
}

interface WebsiteRow {
  id: string;
  url: string;
  name: string;
  schema_enabled: boolean | null;
}

const emptyPage = (): Omit<PageModel, "kind" | "id" | "path" | "title" | "bodyHtml"> => ({
  sourceTable: null,
  sourceId: null,
  sourceUrl: null,
  seoTitle: null,
  metaDescription: null,
  excerpt: null,
  featuredImageUrl: null,
  datePublished: null,
  dateModified: null,
  categorySlug: null,
  categoryName: null,
  schemaGraph: null,
  schemaWarnings: [],
  schemaErrors: [],
});

export async function assembleSite(mapId: string, opts: AssembleOptions = {}): Promise<SiteModel> {
  const warnings: string[] = [];
  const progress = opts.onProgress || (() => {});
  progress({ type: "phase", phase: "assemble", message: "Wczytuję dane mapy" });

  const { data: map, error: mapErr } = await (supabase as any)
    .from("thematic_maps")
    .select("id, name, language, website_id, active_profile_id, workspace, wp_posts_synced_at, pillar_pages_enabled")
    .eq("id", mapId)
    .single();
  if (mapErr || !map) throw new Error(`Mapa ${mapId} nie istnieje`);
  if (!map.website_id) throw new Error("Mapa nie ma przypisanej domeny (website_id) — przypisz domenę w ustawieniach mapy");

  const { data: website } = await (supabase as any)
    .from("websites")
    .select("id, url, name, schema_enabled")
    .eq("id", map.website_id)
    .single();
  if (!website?.url) throw new Error("Domena mapy nie ma adresu URL (websites.url)");
  const site = website as WebsiteRow;

  const domain = domainFromUrl(site.url);
  const baseUrl = baseUrlForDomain(domain);
  const settings: StaticSiteSettings = { ...DEFAULT_SITE_SETTINGS, ...(opts.settings || {}) };
  const catBase = settings.category_base ?? "category"; // "" = kategorie bez przedrostka

  // --- WP REST: wpisy + strony + kategorie (zrodlo prawdy dla tego, co jest na WP) ---
  let wpPosts: WpRestPost[] = [];
  let wpPages: WpRestPage[] = [];
  let wpCats: WpRestCategory[] = [];
  let wpRestOk = false;
  if (opts.fetchWpRest !== false) {
    progress({ type: "phase", phase: "assemble", message: "Pobieram wpisy, strony i kategorie z WordPressa" });
    const results = await Promise.allSettled([fetchWpPosts(site.url), fetchWpPages(site.url), fetchWpCategories(site.url)]);
    if (results[0].status === "fulfilled") { wpPosts = results[0].value; wpRestOk = true; }
    else warnings.push(`WP REST wpisy niedostępne: ${results[0].reason?.message || results[0].reason} — stare wpisy z tabeli wp_posts (może być nieaktualna).`);
    if (results[1].status === "fulfilled") wpPages = results[1].value;
    else warnings.push(`WP REST strony niedostępne: ${results[1].reason?.message || results[1].reason}.`);
    if (results[2].status === "fulfilled") wpCats = results[2].value;
    else warnings.push(`WP REST kategorie niedostępne: ${results[2].reason?.message || results[2].reason} — kategorie z seedów mapy.`);
  }
  const wpCatById = new Map<number, WpRestCategory>();
  for (const c of wpCats) wpCatById.set(c.id, c);
  const wpPostByPath = new Map<string, WpRestPost>();
  for (const p of wpPosts) if (p.link) wpPostByPath.set(pathFromUrl(p.link), p);

  // --- Seeds (fallback kategorii, gdy WP REST nie odpowiedzial) ---------------
  const [{ data: seeds }, { data: seedCats }] = await Promise.all([
    (supabase as any).from("thematic_seeds").select("id, name, slug, hub_title, hub_slug_override, hub_content").eq("map_id", mapId).order("sort_order"),
    (supabase as any).from("thematic_seed_wp_categories").select("seed_id, wp_category_id, wp_category_slug, wp_category_name").eq("map_id", mapId),
  ]);
  const seedCatMap = new Map<string, { id?: number; slug: string; name: string }>();
  for (const sc of (seedCats || []) as Array<{ seed_id: string; wp_category_id?: number; wp_category_slug?: string; wp_category_name?: string }>) {
    if (sc.wp_category_slug) seedCatMap.set(sc.seed_id, { id: sc.wp_category_id, slug: sc.wp_category_slug, name: sc.wp_category_name || sc.wp_category_slug });
  }
  const seedInfo = new Map<string, { slug: string; name: string; description: string | null }>();
  for (const s of (seeds || []) as Array<{ id: string; name: string; slug: string; hub_title?: string; hub_slug_override?: string; hub_content?: string }>) {
    const fromWp = seedCatMap.get(s.id);
    const slug = fromWp?.slug || s.hub_slug_override || s.slug;
    const name = fromWp?.name || s.hub_title || s.name;
    if (slug) seedInfo.set(s.id, { slug, name, description: s.hub_content ? excerptFromHtml(s.hub_content, 200) : null });
  }

  /** Kategoria dla wpisu WP: pierwsza z listy categories (z pominieciem "bez kategorii"). */
  const catOfWpPost = (p: WpRestPost | undefined): WpRestCategory | null => {
    if (!p) return null;
    for (const id of p.categories) {
      const c = wpCatById.get(id);
      if (c && c.slug !== "bez-kategorii" && c.slug !== "uncategorized") return c;
    }
    const first = p.categories.length ? wpCatById.get(p.categories[0]) : undefined;
    return first || null;
  };

  // --- Nodes + content ----------------------------------------------------------
  const [{ data: nodes }, { data: contents }] = await Promise.all([
    (supabase as any).from("thematic_nodes").select("id, title, slug, seed_id, document_role, node_role, matched_wp_post_id").eq("map_id", mapId),
    (supabase as any).from("thematic_node_content")
      .select("node_id, final_html, seo_title, meta_description, featured_image_url, effective_publish_date, published_at, updated_at, wp_post_url, engine_stage, opening_answer")
      .eq("map_id", mapId),
  ]);
  const contentByNode = new Map<string, any>();
  for (const c of contents || []) contentByNode.set(c.node_id, c);

  const pages: PageModel[] = [];
  const publishedNodes = ((nodes || []) as any[]).filter((n) => {
    const c = contentByNode.get(n.id);
    return c && c.engine_stage === "published" && c.final_html;
  });
  const readyNotPublished = ((nodes || []) as any[]).filter((n) => {
    const c = contentByNode.get(n.id);
    return c && c.final_html && c.engine_stage !== "published";
  }).length;
  if (readyNotPublished) warnings.push(`${readyNotPublished} artykułów ma gotową treść, ale nie jest opublikowanych — pomijam (opublikuj je w kroku Publikacja).`);
  if (!publishedNodes.length) warnings.push("Mapa nie ma żadnego opublikowanego artykułu.");

  progress({ type: "phase", phase: "assemble", message: `Składam ${publishedNodes.length} artykułów + Schema.org` });
  let done = 0;
  for (const n of publishedNodes) {
    const c = contentByNode.get(n.id);
    const isPillar = n.document_role === "pillar" || n.node_role === "pillar";
    let schemaGraph = null;
    let schemaWarnings: string[] = [];
    let schemaErrors: string[] = [];
    if (site.schema_enabled !== false) {
      try {
        const payload = await generateSchemasForNode(n.id);
        schemaGraph = payload.graph;
        schemaWarnings = payload.warnings;
        schemaErrors = payload.errors;
      } catch (e) {
        schemaErrors = [e instanceof Error ? e.message : String(e)];
      }
    }
    const sourceUrl: string | null = c.wp_post_url || null;
    const path = sourceUrl && isSiteHost(hostOf(sourceUrl), domain) ? pathFromUrl(sourceUrl) : pathForSlug(baseUrl, n.slug);
    if (sourceUrl && !isSiteHost(hostOf(sourceUrl), domain)) {
      warnings.push(`${n.slug}: wp_post_url wskazuje na inny host (${hostOf(sourceUrl)}) — użyto ścieżki ze sluga`);
    }
    // Kategoria: to, w czym wpis FAKTYCZNIE siedzi na WP; fallback seed
    const wpPost = wpPostByPath.get(path);
    const wpCat = catOfWpPost(wpPost);
    const seed = n.seed_id ? seedInfo.get(n.seed_id) : undefined;
    pages.push({
      ...emptyPage(),
      kind: isPillar ? "pillar" : "article",
      id: n.id,
      sourceTable: "thematic_node_content",
      sourceId: n.id,
      path,
      sourceUrl,
      title: n.title,
      seoTitle: c.seo_title || null,
      metaDescription: c.meta_description || null,
      bodyHtml: stripLdJson(c.final_html),
      excerpt: c.opening_answer ? excerptFromHtml(c.opening_answer, 160) : null,
      featuredImageUrl: c.featured_image_url || wpPost?.featuredImageUrl || null,
      datePublished: c.effective_publish_date || c.published_at || wpPost?.date || null,
      dateModified: c.updated_at || c.published_at || null,
      categorySlug: wpCat?.slug || seed?.slug || null,
      categoryName: wpCat?.name || seed?.name || null,
      schemaGraph,
      schemaWarnings,
      schemaErrors,
    });
    done++;
    if (done % 10 === 0 || done === publishedNodes.length) {
      progress({ type: "progress", phase: "assemble", current: done, total: publishedNodes.length, message: "artykuły" });
    }
  }

  // --- Autor -------------------------------------------------------------------
  let author: AuthorModel | null = null;
  let profile: any = null;
  if (map.active_profile_id) {
    const { data } = await (supabase as any)
      .from("thematic_writing_profiles")
      .select("id, name, profile_name, public_author_label, author_identity, author_slug, author_url, author_photo_url, author_bio_html, author_page_html, author_page_wp_post_id, author_page_generated_at, author_page_enabled, schema_enabled, author_same_as, author_job_title")
      .eq("id", map.active_profile_id)
      .maybeSingle();
    profile = data;
  } else {
    warnings.push("Mapa nie ma aktywnego profilu autora — strona bez sekcji autora.");
  }
  const { data: persona } = await (supabase as any).from("map_persona").select("full_name, service_label, social_links").eq("map_id", mapId).maybeSingle();

  const authorWpLink = profile?.author_page_wp_post_id
    ? wpPages.find((p) => p.id === profile.author_page_wp_post_id)?.link || null
    : null;

  if (profile) {
    const name = profile.profile_name || profile.name || profile.public_author_label || persona?.full_name || profile.author_identity || site.name;
    const aPath = authorPath({ wpLink: authorWpLink, author_slug: profile.author_slug, public_author_label: name });
    const sameAs: string[] = [];
    for (const l of [...(Array.isArray(profile.author_same_as) ? profile.author_same_as : []), ...(Array.isArray(persona?.social_links) ? persona.social_links : [])]) {
      const u = typeof l === "string" ? l : l?.url;
      if (u && /^https?:\/\//.test(u) && !sameAs.includes(u)) sameAs.push(u);
    }
    author = {
      name,
      slug: profile.author_slug || "",
      path: profile.author_page_html ? aPath : null,
      photoUrl: profile.author_photo_url || null,
      bioHtml: profile.author_bio_html || null,
      jobTitle: persona?.service_label || profile.author_job_title || null,
      sameAs,
    };
    if (profile.author_page_html && aPath) {
      pages.push({
        ...emptyPage(),
        kind: "author",
        id: `author-${profile.id}`,
        sourceTable: "thematic_writing_profiles",
        sourceId: profile.id,
        path: aPath,
        sourceUrl: authorWpLink,
        title: name,
        metaDescription: profile.author_bio_html ? excerptFromHtml(profile.author_bio_html) : null,
        bodyHtml: stripLdJson(profile.author_page_html),
        featuredImageUrl: profile.author_photo_url || null,
        datePublished: profile.author_page_generated_at || null,
        dateModified: profile.author_page_generated_at || null,
      });
    }
  }

  // --- Unikalne podstrony ----------------------------------------------------
  const { data: subpages } = await (supabase as any)
    .from("unique_subpages")
    .select("id, kind, title, html, status, wp_page_url, updated_at, created_at")
    .eq("map_id", mapId)
    .in("status", ["ready", "published"]);
  for (const sp of (subpages || []) as any[]) {
    if (!sp.html) continue;
    pages.push({
      ...emptyPage(),
      kind: "subpage",
      id: sp.id,
      sourceTable: "unique_subpages",
      sourceId: sp.id,
      path: subpagePath({ wp_page_url: sp.wp_page_url, title: sp.title }),
      sourceUrl: sp.wp_page_url || null,
      title: sp.title,
      bodyHtml: stripLdJson(sp.html),
      datePublished: sp.created_at || null,
      dateModified: sp.updated_at || null,
    });
  }

  // --- Stare strony WP (legacy_page), ktorych panel nie zna --------------------
  const knownPaths = new Set(pages.map((p) => p.path));
  for (const wp of wpPages) {
    const path = pathFromUrl(wp.link);
    if (knownPaths.has(path)) continue;
    if (profile?.author_page_wp_post_id === wp.id) continue;
    if (!wp.content?.trim()) continue;
    knownPaths.add(path);
    pages.push({
      ...emptyPage(),
      kind: "legacy_page",
      id: `wp-page-${wp.id}`,
      sourceTable: "wp_rest_pages",
      sourceId: String(wp.id),
      path,
      sourceUrl: wp.link,
      title: wp.title,
      metaDescription: wp.excerpt ? excerptFromHtml(wp.excerpt) : null,
      bodyHtml: cleanWpContent(wp.content),
      datePublished: wp.date || null,
      dateModified: wp.modified || null,
    });
  }

  // --- Stare wpisy WP spoza mapy (legacy_post) — z REST; fallback wp_posts ------
  let legacyCount = 0;
  if (wpRestOk) {
    for (const wp of wpPosts) {
      const path = pathFromUrl(wp.link);
      if (knownPaths.has(path)) continue;
      if (!wp.content?.trim()) continue;
      knownPaths.add(path);
      legacyCount++;
      const cat = catOfWpPost(wp);
      pages.push({
        ...emptyPage(),
        kind: "legacy_post",
        id: `wp-post-${wp.id}`,
        sourceTable: "wp_rest_posts",
        sourceId: String(wp.id),
        path,
        sourceUrl: wp.link,
        title: wp.title,
        metaDescription: wp.excerpt ? excerptFromHtml(wp.excerpt) : null,
        bodyHtml: cleanWpContent(wp.content),
        excerpt: wp.excerpt ? excerptFromHtml(wp.excerpt) : null,
        featuredImageUrl: wp.featuredImageUrl,
        datePublished: wp.date || null,
        dateModified: wp.modified || wp.date || null,
        categorySlug: cat?.slug || null,
        categoryName: cat?.name || null,
      });
    }
  } else {
    const matchedWpIds = new Set(((nodes || []) as any[]).map((n) => n.matched_wp_post_id).filter(Boolean));
    const { data: dbPosts } = await (supabase as any)
      .from("wp_posts")
      .select("id, title, content, excerpt, published_at, modified_at, featured_image_url, wp_url, categories, meta_description")
      .eq("website_id", map.website_id)
      .eq("status", "publish");
    for (const wp of (dbPosts || []) as any[]) {
      if (matchedWpIds.has(wp.id) || !wp.wp_url || !wp.content) continue;
      const path = pathFromUrl(wp.wp_url);
      if (knownPaths.has(path)) continue;
      knownPaths.add(path);
      legacyCount++;
      pages.push({
        ...emptyPage(),
        kind: "legacy_post",
        id: `wp-post-${wp.id}`,
        sourceTable: "wp_posts",
        sourceId: wp.id,
        path,
        sourceUrl: wp.wp_url,
        title: decodeEntities(wp.title),
        metaDescription: wp.meta_description || (wp.excerpt ? excerptFromHtml(wp.excerpt) : null),
        bodyHtml: cleanWpContent(wp.content),
        excerpt: wp.excerpt ? excerptFromHtml(wp.excerpt) : null,
        featuredImageUrl: wp.featured_image_url || null,
        datePublished: wp.published_at || null,
        dateModified: wp.modified_at || wp.published_at || null,
        categorySlug: firstCategorySlug(wp.categories),
      });
    }
    if (!map.wp_posts_synced_at) warnings.push("WP REST niedostępne i wpisy nigdy nie były synchronizowane — stare wpisy spoza mapy mogą być pominięte.");
  }
  if (legacyCount) warnings.push(`${legacyCount} starych wpisów WP spoza mapy trafi na stronę jako treść archiwalna (${wpRestOk ? "z REST API WP" : "z tabeli wp_posts"}).`);

  // --- Kategorie: z WP (link -> sciezka), fallback seedy -----------------------
  const catCounts = new Map<string, number>();
  for (const pg of pages) if (pg.categorySlug && (pg.kind === "article" || pg.kind === "pillar" || pg.kind === "legacy_post")) catCounts.set(pg.categorySlug, (catCounts.get(pg.categorySlug) || 0) + 1);
  const categories: CategoryModel[] = [];
  const seenCat = new Set<string>();
  for (const c of wpCats) {
    const cnt = catCounts.get(c.slug) || 0;
    if (!cnt || seenCat.has(c.slug)) continue;
    seenCat.add(c.slug);
    categories.push({ slug: c.slug, name: c.name, path: c.link ? pathFromUrl(c.link) : categoryPath(c.slug, catBase), description: c.description || null, count: cnt });
  }
  for (const info of seedInfo.values()) {
    const cnt = catCounts.get(info.slug) || 0;
    if (!cnt || seenCat.has(info.slug)) continue;
    seenCat.add(info.slug);
    categories.push({ slug: info.slug, name: info.name, path: categoryPath(info.slug, catBase), description: info.description, count: cnt });
  }
  for (const [slug, cnt] of catCounts) {
    if (seenCat.has(slug)) continue;
    seenCat.add(slug);
    categories.push({ slug, name: slug.replace(/-/g, " "), path: categoryPath(slug, catBase), description: null, count: cnt });
  }
  for (const pg of pages) if (pg.categorySlug && !pg.categoryName) pg.categoryName = categories.find((c) => c.slug === pg.categorySlug)?.name || null;
  // Kategorie WP bez zadnego wpisu, ktory znamy — ostrzez (G3 pokaze je jako brakujace)
  const emptyWpCats = wpCats.filter((c) => c.count > 0 && !seenCat.has(c.slug));
  if (emptyWpCats.length) warnings.push(`Kategorie na WP bez treści, którą znam: ${emptyWpCats.map((c) => c.slug).join(", ")} — dostaną propozycję przekierowania.`);

  // --- Brand -------------------------------------------------------------------
  const { data: brandRow } = await (supabase as any).from("map_brand_assets").select("logo_svg, theme_color, colors").eq("map_id", mapId).maybeSingle();
  const brand: BrandModel | null = brandRow
    ? { logoSvg: brandRow.logo_svg || null, themeColor: brandRow.theme_color || null, colors: Array.isArray(brandRow.colors) ? brandRow.colors : [] }
    : null;

  // --- Live sitemap WP ----------------------------------------------------------
  let wpSitemapUrls: string[] = [];
  if (opts.fetchWpSitemap !== false) {
    progress({ type: "phase", phase: "assemble", message: "Pobieram sitemap WordPressa" });
    try {
      wpSitemapUrls = await fetchWpSitemapUrls(site.url, site.id, domain);
      if (!wpSitemapUrls.length) warnings.push("Nie znaleziono sitemap WP (wp-sitemap.xml / sitemap_index.xml) — bramka URL oparta o REST + dane panelu.");
    } catch (e) {
      warnings.push(`Sitemap WP niedostępna (Edge Function fetch-asset): ${e instanceof Error ? e.message : String(e)}`);
    }
  }
  // Inwentarz z REST tez wchodzi do G3 (nawet bez sitemap)
  for (const p of wpPosts) if (p.link) wpSitemapUrls.push(p.link);
  for (const p of wpPages) if (p.link) wpSitemapUrls.push(p.link);
  for (const c of wpCats) if (c.link && c.count > 0) wpSitemapUrls.push(c.link);
  wpSitemapUrls = [...new Set(wpSitemapUrls)];

  // --- Menu: ustawienia (po walidacji) -> nav z HTML strony glownej WP -> auto ---
  const menuScope = { categories, pages, author, domain };
  let menu = settings.menu ? validateMenu(settings.menu, menuScope) : null;
  if (menu?.warnings.length) warnings.push(...menu.warnings);
  if (!menu && opts.fetchWpRest !== false) {
    progress({ type: "phase", phase: "assemble", message: "Odtwarzam menu ze strony głównej WordPressa" });
    try {
      const homeHtml = await fetchAssetText(`${site.url.replace(/\/$/, "")}/`, site.id);
      const fromWp = buildMenuFromWp(homeHtml, menuScope);
      if (fromWp) menu = { menu: fromWp, warnings: [] };
      else warnings.push("Nie rozpoznano menu na stronie głównej WP — menu zbudowane automatycznie ze wszystkich kategorii.");
    } catch (e) {
      warnings.push(`Strona główna WP niedostępna (menu zbudowane automatycznie): ${e instanceof Error ? e.message : String(e)}`);
    }
  }
  const siteMenu = menu?.menu ?? buildMenuAuto(menuScope);

  logPublic(PREFIX, `Złożono stronę ${domain}: ${pages.length} stron (${legacyCount} starych wpisów), ${categories.length} kategorii, menu ${siteMenu.source} (${siteMenu.main.length} pozycji), ${wpSitemapUrls.length} URL w inwentarzu, ${warnings.length} ostrzeżeń`);

  return {
    mapId,
    websiteId: site.id,
    domain,
    baseUrl,
    siteName: site.name || domain,
    language: map.language || "pl",
    settings,
    pages,
    categories,
    author,
    brand,
    menu: siteMenu,
    wpSitemapUrls,
    warnings,
    assembledAt: new Date().toISOString(),
  };
}

/** Wyciąga wcześniej wstrzyknięty JSON-LD z treści (renderer wstawia swój do <head>). */
function stripLdJson(html: string): string {
  return (html || "").replace(/<script type="application\/ld\+json">[\s\S]*?<\/script>\n?/gi, "").trim();
}

function firstCategorySlug(categories: unknown): string | null {
  if (!Array.isArray(categories) || !categories.length) return null;
  const c = categories[0] as any;
  if (typeof c === "string") return c;
  return c?.slug || null;
}

async function fetchWpSitemapUrls(siteUrl: string, websiteId: string, domain: string): Promise<string[]> {
  const base = siteUrl.replace(/\/$/, "");
  const candidates = [`${base}/wp-sitemap.xml`, `${base}/sitemap_index.xml`, `${base}/sitemap.xml`];
  let indexXml = "";
  for (const c of candidates) {
    try {
      indexXml = await fetchAssetText(c, websiteId);
      if (indexXml.includes("<loc>")) break;
      indexXml = "";
    } catch (e) {
      logError(PREFIX, `sitemap ${c} niedostępna`, e);
    }
  }
  if (!indexXml) return [];
  const locs = extractLocs(indexXml);
  const subSitemaps = locs.filter((u) => /\.xml(\?.*)?$/i.test(u));
  if (!subSitemaps.length) return locs.filter((u) => isSiteHost(hostOf(u), domain));
  const urls = new Set<string>();
  for (const sm of subSitemaps.slice(0, 15)) {
    if (/user|author/i.test(sm)) continue;
    try {
      const xml = await fetchAssetText(sm, websiteId);
      for (const u of extractLocs(xml)) if (!/\.xml$/i.test(u) && isSiteHost(hostOf(u), domain)) urls.add(u);
    } catch {
      // pomin
    }
  }
  return [...urls];
}

function extractLocs(xml: string): string[] {
  const out: string[] = [];
  const re = /<loc>\s*([^<\s]+)\s*<\/loc>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml))) out.push(m[1].trim());
  return out;
}
